import { useEffect } from 'react'
import { SEO } from './seo-config'
import { applyPageMeta } from './head'

// ─── Structured Data ──────────────────────────────────────────────────────────

const PERSON_ID = `${SEO.portfolioURL}/#person`

function buildPersonSchema() {
  return {
    '@type':        'Person',
    '@id':          PERSON_ID,
    name:           SEO.name,
    alternateName:  SEO.alternateNames,
    givenName:      SEO.givenName,
    familyName:     SEO.familyName,
    jobTitle:       SEO.jobTitle,
    description:    SEO.bio,
    url:            `${SEO.portfolioURL}/`,
    mainEntityOfPage: { '@id': `${SEO.portfolioURL}/#profilepage` },
    email:          SEO.email,
    image:          SEO.profilePhoto,
    address: {
      '@type':          'PostalAddress',
      addressLocality:  SEO.city,
      addressRegion:    SEO.state,
      addressCountry:   SEO.country,
    },
    sameAs:      SEO.sameAs,
    knowsAbout:  SEO.skills,
    alumniOf:    SEO.education.map(e => ({
      '@type':    'EducationalOrganization',
      name:       e.institution,
      url:        e.url,
      location:   e.location,
    })),
    hasCredential: SEO.education.map(e => ({
      '@type':                          'EducationalOccupationalCredential',
      credentialCategory:               e.degree,
      educationalLevel:                 e.field,
      recognizedBy: { '@type': 'EducationalOrganization', name: e.institution },
    })),
    // Publications referenced by @id (full nodes below), code projects inline
    creator: [
      ...SEO.publications.map(p => ({ '@id': `${SEO.portfolioURL}/#${p.id}` })),
      ...SEO.projects.map(p => ({
        '@type':       p.type,
        name:          p.name,
        description:   p.description,
        url:           p.url,
        author:        { '@id': PERSON_ID },
      })),
    ],
  }
}

function buildPublicationSchemas() {
  return SEO.publications.map(p => ({
    '@type':     'ScholarlyArticle',
    '@id':       `${SEO.portfolioURL}/#${p.id}`,
    headline:    p.title,
    name:        p.title,
    description: p.description,
    author: p.authors.map(a =>
      a === SEO.name ? { '@id': PERSON_ID } : { '@type': 'Person', name: a },
    ),
    ...(p.publisher     ? { publisher: { '@type': 'Organization', name: p.publisher } } : {}),
    ...(p.datePublished ? { datePublished: p.datePublished } : {}),
    ...(p.doi ? {
      identifier: { '@type': 'PropertyValue', propertyID: 'DOI', value: p.doi },
      sameAs: [
        `https://doi.org/${p.doi}`,
        ...(p.publisherLink ? [p.publisherLink] : []),
      ],
    } : {}),
    keywords: p.keywords.join(', '),
    url: `${SEO.portfolioURL}/publication/${p.slug}/`,
  }))
}

function buildProfilePageSchema() {
  return {
    '@type':      'ProfilePage',
    '@id':        `${SEO.portfolioURL}/#profilepage`,
    url:          `${SEO.portfolioURL}/`,
    name:         SEO.pages.home.title,
    description:  SEO.pages.home.description,
    inLanguage:   'en-US',
    dateModified: SEO.lastUpdated,
    mainEntity:   { '@id': PERSON_ID },
    about:        { '@id': PERSON_ID },
    isPartOf:     { '@id': `${SEO.portfolioURL}/#website` },
  }
}

function buildWebsiteSchema() {
  return {
    '@type':    'WebSite',
    '@id':      `${SEO.portfolioURL}/#website`,
    name:       SEO.name,
    url:        `${SEO.portfolioURL}/`,
    description: SEO.pages.home.description,
    inLanguage:  'en-US',
    author:      { '@id': PERSON_ID },
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

export const SeoHead = () => {
  const page = SEO.pages.home

  useEffect(() => {
    applyPageMeta({ title: page.title, description: page.description, canonical: page.canonical, type: 'profile' })
  }, [page])

  // JSON-LD is rendered into the document tree — Google accepts it anywhere
  // in the page and indexes it on both initial HTML and JS-rendered content.
  const jsonLd = JSON.stringify({
    '@context': 'https://schema.org',
    '@graph': [
      buildPersonSchema(),
      ...buildPublicationSchemas(),
      buildProfilePageSchema(),
      buildWebsiteSchema(),
    ],
  })

  return (
    <script
      id="structured-data"
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: jsonLd }}
    />
  )
}
